import React, { useEffect, useRef } from 'react';
import { observer } from 'src/utils/mobx-react';
import { GeniusLivePlayerState } from './GeniusLivePlayerState';
import { GeniusLivePlayerContainer, GeniusLivePlayerWrapper } from './GeniusLivePlayer.style';
import { GENIUS_MESSAGE_BUS_EVENT } from './geniusLivePlayer.types';
import { streaming } from 'src_server/trpc/types';

interface GeniusLivePlayerPropsType {
    state: GeniusLivePlayerState;
    eventId: number;
    onClaimsLeadChange?: (claimsLead: boolean) => void;
    /** Fired once metadata is ready and the stream cannot be played */
    onStreamUnavailable?: (availability: streaming.GeniusAvailabilityType | null) => void;
}

export const GeniusLivePlayer = observer(
    'GeniusLivePlayer',
    ({ state, eventId, onClaimsLeadChange, onStreamUnavailable }: GeniusLivePlayerPropsType) => {
        const scriptRef = useRef<HTMLScriptElement | null>(null);
        const listenerRef = useRef<((event: Event) => void) | null>(null);
        const unavailableNotifiedRef = useRef(false);

        const metadataResult = state.metadataResult;

        useEffect(() => {
            if (onClaimsLeadChange === undefined) {
                return;
            }

            onClaimsLeadChange(state.claimsLead);
        }, [onClaimsLeadChange, state.claimsLead]);

        // Sync metadata → uiStatus first, then notify parent only on terminal failure states
        // (avoids a false "unavailable" while uiStatus is still "loading" after metadata becomes ready)
        useEffect(() => {
            state.syncUiStatusFromMetadata();

            if (onStreamUnavailable === undefined || unavailableNotifiedRef.current) {
                return;
            }

            if (metadataResult.type === 'loading') {
                return;
            }

            if (
                metadataResult.type === 'error' ||
                state.uiStatus === 'unavailable' ||
                state.uiStatus === 'playbackError'
            ) {
                unavailableNotifiedRef.current = true;
                onStreamUnavailable(state.availabilityCode);
            }
        }, [onStreamUnavailable, metadataResult.type, state, state.uiStatus, state.availabilityCode]);

        useEffect(() => {
            if (!state.canMountPlayer) {
                return;
            }

            const src = state.getWidgetLoaderSrc();
            if (src === null) {
                return;
            }

            const handleEvent = (event: Event): void => {
                void state.handleGeniusMessage(event);
            };

            listenerRef.current = handleEvent;
            window.addEventListener(GENIUS_MESSAGE_BUS_EVENT, handleEvent);

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.id = `genius-live-player-loader-${eventId}`;
            document.body.appendChild(script);
            scriptRef.current = script;

            return (): void => {
                if (listenerRef.current !== null) {
                    window.removeEventListener(GENIUS_MESSAGE_BUS_EVENT, listenerRef.current);
                    listenerRef.current = null;
                }

                state.closePlayer();

                if (scriptRef.current !== null) {
                    scriptRef.current.remove();
                    scriptRef.current = null;
                }
            };
        }, [state, state.canMountPlayer, eventId]);

        if (metadataResult.type === 'loading') {
            return (
                <GeniusLivePlayerWrapper
                    maxSizePercentage={100}
                    isVisible={true}
                    data-test='genius-live-player-loading'
                >
                    <GeniusLivePlayerContainer />
                </GeniusLivePlayerWrapper>
            );
        }

        if (!state.canMountPlayer) {
            return null;
        }

        return (
            <GeniusLivePlayerWrapper
                maxSizePercentage={state.maxPlayerSizePercentage}
                isVisible={true}
                data-test='genius-live-player'
            >
                <GeniusLivePlayerContainer id={state.containerId} />
            </GeniusLivePlayerWrapper>
        );
    }
);
